/**
 * MoneyInput（LeapWorldTwin-139）：元输入、分存储。解析全部走 `@bf/format` 的
 * `parseYuanToCents`，本文件只管渲染。拒绝科学计数法与超过两位小数。
 */
import { forwardRef, useEffect, useState, type InputHTMLAttributes } from 'react'
import { centsToYuanInput, parseYuanToCents } from '@bf/format'
import { Input } from './Input'

type MoneyInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  /** 分；空输入为 null */
  value: number | null
  onChange: (cents: number | null) => void
  error?: boolean
}

export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { value, onChange, error = false, name, onBlur, ...props },
  ref
) {
  const [text, setText] = useState(() => (value == null ? '' : centsToYuanInput(value)))
  const [parseError, setParseError] = useState<string | null>(null)

  useEffect(() => {
    if (value == null) return
    setText((current) => {
      const parsed = parseYuanToCents(current)
      return parsed.cents === value ? current : centsToYuanInput(value)
    })
  }, [value])

  const commit = (next: string) => {
    setText(next)
    if (!next.trim()) {
      setParseError(null)
      onChange(null)
      return
    }
    const result = parseYuanToCents(next)
    if (result.reason) {
      setParseError(result.message)
      onChange(null)
      return
    }
    setParseError(null)
    onChange(result.cents)
  }

  return (
    <>
      {name ? <input type="hidden" name={name} value={value ?? ''} /> : null}
      <Input
        {...props}
        ref={ref}
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        value={text}
        error={error || Boolean(parseError)}
        aria-invalid={error || Boolean(parseError) || undefined}
        data-parse-error={parseError ?? undefined}
        onChange={(event) => commit(event.target.value)}
        onBlur={(event) => {
          if (text.trim() && !parseError) {
            const result = parseYuanToCents(text)
            if (result.cents != null) setText(centsToYuanInput(result.cents))
          }
          onBlur?.(event)
        }}
      />
    </>
  )
})
